import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailCampaignPreview() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    fetchPreview();
  }, [campaignId]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Obtener campaña y preview
      const [campaignRes, previewRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/email-campaigns`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.post(
          `${BACKEND_URL}/api/email-campaigns/${campaignId}/preview`,
          { campaign_id: campaignId },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);

      const campaignData = campaignRes.data.find(c => c.id === campaignId);
      setCampaign(campaignData);
      setPreview(previewRes.data);
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Error al cargar preview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/email-campaigns')}
          variant="ghost"
          className="mb-4 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Eye className="h-8 w-8 text-indigo-600" />
              Preview de Campaña
            </h1>
            {campaign && (
              <p className="text-gray-600 mt-2">{campaign.name}</p>
            )}
          </div>
          
          <Button
            onClick={() => navigate(`/email-campaigns/${campaignId}/edit`)}
            className="gap-2"
          >
            Editar Campaña
          </Button>
        </div>
      </div>

      {preview && (
        <div className="space-y-6">
          {/* Subject */}
          <Card>
            <CardHeader>
              <CardTitle>Asunto del Email</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-medium">{preview.subject}</p>
            </CardContent>
          </Card>

          {/* Variables usadas */}
          <Card>
            <CardHeader>
              <CardTitle>Variables Utilizadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(preview.variables_used).map(([key, value]) => (
                  <div key={key} className="text-sm">
                    <span className="font-mono text-indigo-600">{'{{' + key + '}}'}</span>
                    <span className="text-gray-600 ml-2">→ {value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* HTML Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Vista Previa del Email</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden bg-gray-50">
                <iframe
                  srcDoc={preview.html}
                  className="w-full"
                  style={{ height: '600px' }}
                  title="Email Preview"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
